import { supabase } from './supabase';

export interface Referral {
  id: string;
  prospect_id: string;
  original_email: string | null;
  referred_email: string;
  status: 'pending' | 'contacted' | 'converted';
  notes?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Service pour gérer les redirections d'emails
 */
const referralService = {
  /**
   * Récupère toutes les redirections
   */
  async getAllReferrals(): Promise<Referral[]> {
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des redirections:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Récupère les redirections avec un statut spécifique
   */
  async getReferralsByStatus(status: 'pending' | 'contacted' | 'converted'): Promise<Referral[]> {
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(`Erreur lors de la récupération des redirections avec le statut ${status}:`, error);
      throw error;
    }

    return data || [];
  },

  /**
   * Récupère une redirection par ID
   */
  async getReferralById(id: string): Promise<Referral | null> {
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // PGRST116 est l'erreur retournée lorsqu'aucun enregistrement n'est trouvé
        return null;
      }
      console.error(`Erreur lors de la récupération de la redirection avec l'ID ${id}:`, error);
      throw error;
    }

    return data;
  },

  /**
   * Récupère les redirections pour un prospect spécifique
   */
  async getReferralsByProspectId(prospectId: string): Promise<Referral[]> {
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('prospect_id', prospectId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(`Erreur lors de la récupération des redirections pour le prospect ${prospectId}:`, error);
      throw error;
    }

    return data || [];
  },

  /**
   * Crée une nouvelle redirection
   */
  async createReferral(referralData: {
    prospect_id: string;
    original_email?: string;
    referred_email: string;
    status?: 'pending' | 'contacted' | 'converted';
    notes?: string;
  }): Promise<Referral> {
    const { data, error } = await supabase
      .from('referrals')
      .insert({
        prospect_id: referralData.prospect_id,
        original_email: referralData.original_email || null,
        referred_email: referralData.referred_email,
        status: referralData.status || 'pending',
        notes: referralData.notes
      })
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la création de la redirection:', error);
      throw error;
    }

    return data;
  },

  /**
   * Met à jour le statut d'une redirection
   */
  async updateReferralStatus(
    id: string, 
    status: 'pending' | 'contacted' | 'converted', 
    notes?: string
  ): Promise<void> {
    const updateData: { status: string; notes?: string } = { status };
    if (notes !== undefined) {
      updateData.notes = notes;
    }

    const { error } = await supabase
      .from('referrals')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error(`Erreur lors de la mise à jour du statut de la redirection ${id}:`, error);
      throw error;
    }
  },

  /**
   * Supprime une redirection
   */
  async deleteReferral(id: string): Promise<void> {
    const { error } = await supabase
      .from('referrals')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Erreur lors de la suppression de la redirection ${id}:`, error);
      throw error;
    }
  },

  /**
   * Génère un URL de redirection pour un prospect
   */
  generateReferralUrl(token: string): string {
    // Obtenez l'URL de base de l'application depuis l'environnement ou utilisez une valeur par défaut
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    return `${baseUrl}/referral/${token}`;
  }
};

export default referralService;
